import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type ApiClientError, type ApiSuccess } from '@/lib/api-client';
import { EXCEPTIONS_QUERY_KEY } from '@/features/exceptions/useExceptions';
import { NOTIFICATIONS_QUERY_KEY } from '@/features/notifications/useNotifications';
import { FIRST_VALUATION_QUERY_KEY } from '@/features/valuation/first-valuation/useFirstValuation';
import {
  fetchInboundLots,
  fetchInboundLot,
  fetchInboundLotWithPackages,
  confirmInboundLot,
  createInboundLot,
  createInboundLotWithPackages,
  completeInboundPackage,
  deleteInboundLot,
  deleteInboundPackage,
  fetchDeletedInboundLots,
  updateInboundLotWorksheet,
  bulkAssignWarehouse,
  linkTruckPhoto,
  previewInboundLotDelete,
  removeTruckPhoto,
  restoreDeletedInboundLot,
  setTruckPhotoKind,
  type DeletedInboundLotListResult,
  type InboundListMeta,
} from './api';
import type {
  BulkAssignWarehouseInput,
  CreateInboundLotWithPackagesResponse,
  InboundDeletePreview,
  InboundDeleteResult,
  InboundLotCreateInput,
  InboundLotDto,
  InboundLotWithLinesDto,
  InboundLotWithPackagesDetailDto,
  InboundLotWithPackagesInput,
  InboundLotWorksheetUpdateInput,
  InboundPackageCompleteInput,
  InboundPackageDeleteInput,
  InboundPackageDto,
} from './types';

/**
 * The facility a batch belongs to. Operational warehouse and location pickers
 * need it: the backend only accepts a warehouse from the batch's own facility,
 * so an unscoped picker offers options every submit would reject.
 */
export const useBatchFacilityId = (batchId: string | undefined) => {
  const query = useQuery<string | null, ApiClientError>({
    queryKey: ['monthly-batches', 'facility', batchId],
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ facility_id: string }>>(
        `/monthly-batches/${batchId}`,
      );
      return res.data.data?.facility_id ?? null;
    },
    enabled: !!batchId,
    staleTime: 5 * 60 * 1000,
  });
  return query.data ?? undefined;
};

const KEY = 'inbound-lots' as const;

export const invalidateInboundChainQueries = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: [KEY] });
  void qc.invalidateQueries({ queryKey: ['dispatch-requests'] });
  void qc.invalidateQueries({ queryKey: ['sorting-packages'] });
  void qc.invalidateQueries({ queryKey: ['sorting-decisions'] });
  void qc.invalidateQueries({ queryKey: ['dismantling-packages'] });
  void qc.invalidateQueries({ queryKey: ['dismantling-sessions'] });
  void qc.invalidateQueries({ queryKey: ['inventory'] });
  void qc.invalidateQueries({ queryKey: ['weight-difference-checks'] });
  void qc.invalidateQueries({ queryKey: ['evidence'] });
  void qc.invalidateQueries({ queryKey: ['file-library'] });
  void qc.invalidateQueries({ queryKey: ['supplier-portal'] });
  void qc.invalidateQueries({ queryKey: ['mass-balance'] });
  void qc.invalidateQueries({ queryKey: [FIRST_VALUATION_QUERY_KEY] });
  void qc.invalidateQueries({ queryKey: [EXCEPTIONS_QUERY_KEY] });
  void qc.invalidateQueries({ queryKey: [NOTIFICATIONS_QUERY_KEY] });
  void qc.invalidateQueries({ queryKey: ['correction-requests', 'targets'] });
  void qc.invalidateQueries({ queryKey: ['audit-package', 'precheck'] });
  void qc.invalidateQueries({ queryKey: ['secondary-process-packages'] });
  void qc.invalidateQueries({ queryKey: ['disassembly-batches', 'inbound-packages'] });
  void qc.invalidateQueries({ queryKey: ['monthly-material-valuation'] });
  void qc.invalidateQueries({ queryKey: ['getit-purchase-price'] });
  void qc.invalidateQueries({ queryKey: ['monthly-batches'] });
  void qc.invalidateQueries({ queryKey: ['monthly-reports'] });
  void qc.invalidateQueries({ queryKey: ['dashboard'] });
};

export const useInboundLotsList = (
  batchId: string | undefined,
  params: { page?: number; per_page?: number } = {},
) => {
  return useQuery<{ data: InboundLotDto[]; meta: InboundListMeta }, ApiClientError>({
    queryKey: [KEY, 'list', batchId, params],
    queryFn: () => fetchInboundLots(batchId!, params),
    enabled: !!batchId,
    placeholderData: (prev) => prev,
  });
};

export const useInboundLot = (id: string | undefined) => {
  return useQuery<InboundLotWithLinesDto, ApiClientError>({
    queryKey: [KEY, 'detail', id],
    queryFn: () => fetchInboundLot(id!),
    enabled: !!id,
  });
};

export const useInboundLotWithPackages = (id: string | undefined) => {
  return useQuery<InboundLotWithPackagesDetailDto, ApiClientError>({
    queryKey: [KEY, 'with-packages', id],
    queryFn: () => fetchInboundLotWithPackages(id!),
    enabled: !!id,
  });
};

export const useConfirmInboundLot = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, string>({
    mutationFn: (id) => confirmInboundLot(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useCreateInboundLot = () => {
  const qc = useQueryClient();
  return useMutation<
    InboundLotDto,
    ApiClientError,
    { batchId: string; body: InboundLotCreateInput }
  >({
    mutationFn: ({ batchId, body }) => createInboundLot(batchId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useCreateInboundLotWithPackages = () => {
  const qc = useQueryClient();
  return useMutation<
    CreateInboundLotWithPackagesResponse,
    ApiClientError,
    { batchId: string; body: InboundLotWithPackagesInput }
  >({
    mutationFn: ({ batchId, body }) => createInboundLotWithPackages(batchId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useCompleteInboundPackage = () => {
  const qc = useQueryClient();
  return useMutation<
    InboundPackageDto,
    ApiClientError,
    { packageId: string; body: InboundPackageCompleteInput }
  >({
    mutationFn: ({ packageId, body }) => completeInboundPackage(packageId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeleteInboundPackage = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { packageId: string; body: InboundPackageDeleteInput }>({
    mutationFn: ({ packageId, body }) => deleteInboundPackage(packageId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useDeleteInboundLot = () => {
  const qc = useQueryClient();
  return useMutation<
    InboundDeleteResult,
    ApiClientError,
    { lotId: string; body: InboundPackageDeleteInput }
  >({
    mutationFn: ({ lotId, body }) => deleteInboundLot(lotId, body),
    onSuccess: () => invalidateInboundChainQueries(qc),
  });
};

export const useCancelInboundLot = useDeleteInboundLot;

export const useInboundLotDeletePreview = (lotId: string | undefined, enabled: boolean) => {
  return useQuery<InboundDeletePreview, ApiClientError>({
    queryKey: [KEY, 'delete-preview', lotId],
    queryFn: () => previewInboundLotDelete(lotId!),
    enabled: enabled && !!lotId,
  });
};

export const useInboundLotCancelPreview = useInboundLotDeletePreview;

export const useDeletedInboundLots = (
  params: { monthly_batch_id?: string; page?: number; per_page?: number } = {},
  enabled = true,
) => {
  return useQuery<DeletedInboundLotListResult, ApiClientError>({
    queryKey: [KEY, 'deleted', params],
    queryFn: () => fetchDeletedInboundLots(params),
    enabled,
    placeholderData: (prev) => prev,
  });
};

export const useRestoreDeletedInboundLot = () => {
  const qc = useQueryClient();
  return useMutation<InboundDeleteResult, ApiClientError, string>({
    mutationFn: restoreDeletedInboundLot,
    onSuccess: () => invalidateInboundChainQueries(qc),
    onError: (error) => {
      if ([404, 409, 410].includes(error.status)) {
        void qc.invalidateQueries({ queryKey: [KEY, 'deleted'] });
      }
    },
  });
};

export const useUpdateInboundLotWorksheet = () => {
  const qc = useQueryClient();
  return useMutation<
    InboundLotWithPackagesDetailDto,
    ApiClientError,
    { lotId: string; body: InboundLotWorksheetUpdateInput }
  >({
    mutationFn: ({ lotId, body }) => updateInboundLotWorksheet(lotId, body),
    onSuccess: () => invalidateInboundChainQueries(qc),
  });
};

export const useBulkAssignWarehouse = () => {
  const qc = useQueryClient();
  return useMutation<
    { updated: number },
    ApiClientError,
    { lotId: string; body: BulkAssignWarehouseInput }
  >({
    mutationFn: ({ lotId, body }) => bulkAssignWarehouse(lotId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};

export const useSetTruckPhotoKind = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { lotId: string; evidenceFileId: string; kind: string }>(
    {
      mutationFn: ({ lotId, evidenceFileId, kind }) =>
        setTruckPhotoKind(lotId, { evidence_file_id: evidenceFileId, kind }),
      onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
    },
  );
};

export const useLinkTruckPhoto = () => {
  const qc = useQueryClient();
  return useMutation<void, ApiClientError, { lotId: string; evidenceFileId: string; kind: string }>(
    {
      mutationFn: ({ lotId, evidenceFileId, kind }) =>
        linkTruckPhoto(lotId, { evidence_file_id: evidenceFileId, kind }),
      onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
    },
  );
};

export const useRemoveTruckPhoto = () => {
  const qc = useQueryClient();
  return useMutation<
    void,
    ApiClientError,
    { lotId: string; evidenceFileId: string; changeReason?: string }
  >({
    mutationFn: ({ lotId, evidenceFileId, changeReason }) =>
      removeTruckPhoto(lotId, evidenceFileId, { change_reason: changeReason ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
};
